/**
 * STORE-V2-CONVERSION-004 — brand logo registry + asset manifest (no request-time scrape).
 * Official assets only when verified; otherwise text/monogram fallback.
 *
 * STAGING-LAUNCH-READINESS-002-R2-R3 — VERIFIED_BRAND_LOGOS is now populated from
 * scripts/brand-assets-inventory.mjs's generated manifest (mirrors the public
 * apps/mini-app/public/brand-logos/manifest.json map 1:1). No runtime scraping,
 * no hotlinked URLs — every local_asset_path is a same-origin /brand-logos/* path.
 * See docs/catalog/BRAND_LOGO_SOURCE_POLICY.md.
 */
import { GENERATED_VERIFIED_BRAND_LOGOS } from "./brand-logo-manifest-v2.generated.js";

export type BrandAssetSource =
  | "official_brand_kit"
  | "official_manufacturer_site"
  | "supplier_provided"
  | "packaging_derived"
  | "text_fallback";

export type BrandAssetUsageStatus = "active" | "pending" | "rejected" | "fallback_only";
export type BrandAssetReviewerStatus = "unreviewed" | "approved" | "rejected";

export interface BrandLogoRecord {
  canonical_slug: string;
  brand_name: string;
  local_asset_path: string | null;
  official_source: string | null;
  source_type: "official_website" | "media_kit" | "manufacturer" | "authorized_distributor" | "supplier" | null;
  source: BrandAssetSource;
  checksum: string | null;
  imported_at: string | null;
  usage_status: BrandAssetUsageStatus;
  reviewer_status: BrandAssetReviewerStatus;
  verified_at: string | null;
  rights_note: string;
  asset_hash: string | null;
}

/**
 * Verified logos only. Populated from the generated inventory manifest — never
 * hand-invented. Every entry's local_asset_path must resolve to a same-origin
 * file under apps/mini-app/public/brand-logos/ (enforced by the R2-R3 guard test).
 */
export const VERIFIED_BRAND_LOGOS: BrandLogoRecord[] = [...GENERATED_VERIFIED_BRAND_LOGOS];

export const BRAND_ASSET_MANIFEST = {
  schema: "asiastyle.brand_assets.v1",
  updated_on: "2026-07-24",
  policy: [
    "Do not scrape logos at customer request time",
    "Do not invent or redraw brand marks",
    "Priority: manufacturer > official media kit > local curated folder > authorized distributor > social/aggregator",
  ],
  assets: VERIFIED_BRAND_LOGOS,
} as const;

export function resolveBrandLogo(slug: string): BrandLogoRecord | null {
  const hit = VERIFIED_BRAND_LOGOS.find(
    (row) =>
      row.canonical_slug === slug &&
      row.local_asset_path &&
      row.usage_status === "active" &&
      row.reviewer_status === "approved",
  );
  return hit ?? null;
}

export function brandMonogram(name: string): string {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
}

export function textFallbackLogoRecord(slug: string, name: string): BrandLogoRecord {
  return {
    canonical_slug: slug,
    brand_name: name,
    local_asset_path: null,
    official_source: null,
    source_type: null,
    source: "text_fallback",
    checksum: null,
    imported_at: null,
    usage_status: "fallback_only",
    reviewer_status: "unreviewed",
    verified_at: null,
    rights_note: "Text monogram fallback until official asset is approved",
    asset_hash: null,
  };
}
